<script setup lang="ts">
import { computed, toRef } from 'vue'
import UiIcon from '@/components/common/UiIcon.vue'
import { useCta, type CtaOptions } from '@/composables/useCta'

const props = defineProps<{
  options: CtaOptions
}>()

// Keep options reactive (important for locale changes, etc.)
const cta = useCta(toRef(props, 'options'))

// Unwrap composable refs to plain values for template/TS compatibility
const isInternal = computed(() => cta.isInternal.value)
const isScroll = computed(() => cta.isScroll.value)

const routerTo = computed(() => cta.routerTo.value)
const href = computed(() => cta.href.value)
const target = computed(() => cta.target.value)
const rel = computed(() => cta.rel.value)

const colorClass = computed(() => cta.colorClass.value)
const text = computed(() => cta.text.value)
const iconName = computed(() => cta.icon)
const appearance = computed(() => cta.appearance.value)
const iconPosition = computed(() => cta.iconPosition.value)
const baseClass = computed(() =>
  appearance.value === 'link'
    ? 'link-ui'
    : 'btn-ui'
)

// Keep the handler stable
function onClick(e: Event) {
  void cta.onClick(e)
}
</script>

<template>
  <!-- Internal link -->
  <RouterLink
    v-if="isInternal && routerTo"
    :to="routerTo"
    :class="[baseClass, colorClass]"
  >
    <UiIcon v-if="iconName && iconPosition === 'left'" :name="iconName" class="size-6" />
    <span v-html="text" />
    <UiIcon v-if="iconName && iconPosition !== 'left'" :name="iconName" class="size-6" />
  </RouterLink>

  <!-- Scroll -->
  <button
    v-else-if="isScroll"
    type="button"
    @click="onClick"
    :class="[baseClass, colorClass, 'cursor-pointer']"
  >
    <UiIcon v-if="iconName && iconPosition === 'left'" :name="iconName" class="size-6" />
    <span v-html="text" />
    <UiIcon v-if="iconName && iconPosition !== 'left'" :name="iconName" class="size-6" />
  </button>

  <!-- External / Email / Phone -->
  <a
    v-else
    :href="href"
    :target="target"
    :rel="rel"
    :class="[baseClass, colorClass]"
  >
    <UiIcon v-if="iconName && iconPosition === 'left'" :name="iconName" class="size-6" />
    <span v-html="text" />
    <UiIcon v-if="iconName && iconPosition !== 'left'" :name="iconName" class="size-6" />
  </a>
</template>
